import React from 'react';
import { Vector3 } from 'three';
import first from 'lodash/first';

import { Copper, defaultInnerCopper } from '../models/Copper';
import { Core, anonymousCore } from '../models/Core';
import { Prepreg, anonymousPrepreg } from '../models/Prepreg';
import createCoreCap from '../models/CoreCap';
import createFoil from '../models/Foil';
import CopperComponent from '../components/common/materials/Copper';
import CoreComponent from '../components/common/materials/Core';
import PrepregComponent from '../components/common/materials/Prepreg';
import {
  FOIL,
  CORE_CAP,
  DEFAULT_INNER_COPPER_WEIGHT,
  CORE,
  PREPREG,
  COPPER,
} from '../constants/globals';
import { validate as validateCopper } from './copper';
// import { validate as validateCore } from './core';
// import { validate as validatePrepreg } from './prepreg';

const constructorMap = {
  [COPPER]: Copper,
  [PREPREG]: Prepreg,
  [CORE]: Core, 
}

/*
 *spec = {
  stackupType,
  numberOfCopperLayers,
  innerCopperWeight,
  outerCopperWeight,
  core,
  prepreg,
 }
**/

export function newStackup(spec = {}) {
  const stackup = {
    name: spec.projectName,
    partNumber: spec.partNumber,
    type: spec.stackupType,
    layers: buildLayers(spec),
    newStackupSpec: spec,
  }

  return stackup;
}

export function buildLayers(spec = {}) {
  const {
    stackupType,
    numberOfCopperLayers,
    innerCopperWeight,
    outerCopperWeight,
    core: defaultCore,
    prepreg: defaultPrepreg,
  } = spec;

  let layers;
  switch (stackupType){
    case FOIL:
      layers = createFoil({
        numberOfCopperLayers,
        innerCopperWeight,
        outerCopperWeight,
        defaultCore,
        defaultPrepreg,
      });
      break;
    case CORE_CAP:
      layers = createCoreCap({
        numberOfCopperLayers,
        innerCopperWeight,
        outerCopperWeight,
        defaultCore,
        defaultPrepreg,
      });
      break;
    default:
      layers = [];
  }

  const indexedLayers = decorateLayers(layers)
  const validations = validateLayers(indexedLayers);
  validations.forEach((validation, index) => {
    const { warnings, errors } = validation
    indexedLayers[index].warnings = warnings;
    indexedLayers[index].errors = errors;
  });

  return indexedLayers;
}

export function restoreLayerInstances(layers = []) {
  const restored = layers.map(layer => restoreLayerInstance(layer));
  return decorateLayers(restored);
}

export function restoreLayerInstance(layer) {
  const { type } = layer;
  const Constructor = constructorMap[type];

  return Constructor.restore(layer);
}

export function mapLayerInstancesToComponents(
  instances= [],
  onLayerClicked,
  onCreate,
  setHoveredLayer,
  unSetHoveredLayer,
  freezeMouseEvents
) {
  const thickness = totalThickness(instances);
  let lastPosition = thickness / 2;
  // create an object with key of displayname and value of Copper/Core/Prepreg;
  const components = Object.assign({},
    ...[CopperComponent, CoreComponent, PrepregComponent]
      .map((item)=> ({ [item.displayName]: item })));

  const stackup = instances.map(layer => {
    const positionY = lastPosition - (layer.spaceNeeded / 2);
    lastPosition -= layer.spaceNeeded;
    const { warnings } = layer;

    return React.createElement(
      components[layer.view],
      {
        key: layer.key,
        target: layer,
        "data-type": layer.type,
        onCreate: onCreate,
        position: new Vector3(0, positionY, 0),
        thickness: layer.thickness_px,
        onLayerClicked,
        setHoveredLayer,
        unSetHoveredLayer,
        freezeMouseEvents,
        hasWarning: warnings && warnings.length > 0,
        ...layer.viewProps,
      });
    }
  );

  return stackup;
}

export function totalThickness(layers = []){
  return layers.reduce((acc, cur) => acc + cur.spaceNeeded, 0);
}

export function floor(layers = []) {
   return -(totalThickness(layers) / 2);
}

export function getDefaultCore(stackup){
  if (stackup === undefined || stackup.newStackupSpec === undefined) {
     return anonymousCore();
  }
   return new Core(stackup.newStackupSpec.core);
}

export function getDefaultPrepreg(stackup){
  if (stackup === undefined || stackup.newStackupSpec === undefined) {
     return anonymousPrepreg();
  }
  return new Prepreg(stackup.newStackupSpec.prepreg);
}

export function getDefaultInnerCopper(stackup){
  let weight;
  if (stackup === undefined || stackup.newStackupSpec === undefined) {
    weight = DEFAULT_INNER_COPPER_WEIGHT;
  } else {
    weight = stackup.newStackupSpec.innerCopperWeight;
  }

  return defaultInnerCopper(weight);
}

export function addLayer(layers, spec){
  const { core, prepreg, copper } = spec;
  const layerLength = layers.length;
  const positions = spec.positions
    .map((p)=> p < 0 ? p + layerLength : p)
    .sort((a,b)=> a - b);

  if (layerLength < 3 ){
    // eslint-disable-next-line no-console
    console.log("Can't add layer to improper layers configuration");
    return layers;
  }

  if (positions.length === 0) {
    // eslint-disable-next-line no-console
    console.log("No Position is specified");
    return layers;
  }

  const layersToAdd = buildLayersWithPositionObj(layers, positions, core, prepreg, copper)
  const newLayers = buildNewLayers(layers, positions, layersToAdd)

  const decoratedLayers = decorateLayers(newLayers);
  const validations = validateLayers(decoratedLayers);
  validations.forEach((validation, index) => {
    const { warnings, errors } = validation
    decoratedLayers[index].warnings = warnings;
    decoratedLayers[index].errors = errors;
  });

  return decoratedLayers;
}

export function updateLayer(layers, index, source) {
  const layer = layers[index];
  const instance = restoreLayerInstance(layer)
  const newLayer = instance.update(source);
  const length = layers.length;
  const newLayers = [
    ...(layers.slice(0, index)),
    newLayer,
    ...(layers.slice(index + 1, length))
  ];

  const decoratedLayers = decorateLayers(newLayers);
  const validations = validateLayers(decoratedLayers);
  validations.forEach((validation, index) => {
    const { warnings, errors } = validation
    decoratedLayers[index].warnings = warnings;
    decoratedLayers[index].errors = errors;
  });

  return decoratedLayers;
}

export function deleteLayers(layers, indexi) {
  const newLayers = layers.map((layer, index) => {
    if (indexi.indexOf(index) >= 0) {
      return undefined;
    }
    return layer;
  }).filter(layer => layer !== undefined);

  const decoratedLayers = decorateLayers(newLayers);
  const validations = validateLayers(decoratedLayers);
  validations.forEach((validation, index) => {
    const { warnings, errors } = validation
    decoratedLayers[index].warnings = warnings;
    decoratedLayers[index].errors = errors;
  });

  return decoratedLayers;
}

export function calculateExtraCoreCount(stackupType, coreCount) {
  if (stackupType === FOIL) {
    return coreCount - 1;
  }
  return coreCount - 2;
}

export function calculateMirrorIndex(lengthOfArray, index) {
  const midPoint = lengthOfArray / 2;
  const distanceFromMidPoint = Math.abs(midPoint - index);

  if(index > midPoint) {
    return index - distanceFromMidPoint * 2 - 1;
  }

  return index + distanceFromMidPoint * 2 - 1;
}

function buildLayersToAdd(positionType, layerAbove, core, prepreg, copper) {

  if (positionType === CORE){
    return [
      new Core(core),
      new Copper(copper),
      new Prepreg([prepreg]),
      new Copper(copper),
    ];
  }
  if (positionType === PREPREG){
    return [
      new Prepreg([prepreg]),
      new Copper(copper),
      new Core(core),
      new Copper(copper),
    ];
  }
  if (positionType === COPPER) {
    // Look at the type of the above layer
    if(layerAbove.type === CORE) {
      return [
         new Copper(copper),
         new Prepreg([prepreg]),
         new Copper(copper),
         new Core(core)
      ];
    }
    if(layerAbove.type === PREPREG) {
      positionType = CORE;
      return [
         new Copper(copper),
         new Core(core),
         new Copper(copper),
         new Prepreg([prepreg])
      ];
    }
  }

  return [];
}

function buildLayersWithPositionObj(layers, positions, core, prepreg, copper){
  return positions.reduce((toAdd, position)=> {
    let layerAbove, positionType;

    // Not allowed to add very top of the outer copper layers;
    if (position === 0) {
      // eslint-disable-next-line no-console
      console.log("Can't add layer to top of the outer copper layer");
      toAdd[position] = [];
    } else {
      positionType = layers[position].type;
      layerAbove = layers[position - 1];
      toAdd[position] = buildLayersToAdd(
        positionType,
        layerAbove,
        core,
        prepreg,
        copper
      );
    }
    return toAdd;
  }, {})
}

function buildNewLayers(currentLayers, positions, layersToAdd){
  const newLayers = [];

  newLayers.push(...currentLayers.slice(0, first(positions)))
  positions.forEach((position, index, arr)=>{
    const nextPosition = arr[index + 1]

    newLayers.push(...layersToAdd[position])

    if (nextPosition !== undefined) {
      newLayers.push(...currentLayers.slice(position, nextPosition))
    } else {
      newLayers.push(...currentLayers.slice(position))
    }
  })

  return newLayers;
}

// decorateLayers add following properties to each layer:
// 1. index,
// 2. typeIndex,
// 3. reference to layers array
function decorateLayers(layers) {
  const indexTypes = {};
  const newLayers = [];
  // 1. Edited Copper not save to local storate. investigate!
  // 4. Display copper weith in Copper Editor

  layers.forEach((layer, index) => {
    const { type } = layer;
    const typeIndex = indexTypes[type] === undefined
      ? 1
      : indexTypes[type];

    indexTypes[type] = typeIndex + 1;
    newLayers.push(
      Object.assign(
        layer,
        {
          index,
          typeIndex,
          // having newLayers here would cause circular dependancy.
          // so adding function instead.
          getLayers: () => newLayers,
        }
      )
    );
  });

  return newLayers;
}

function validateLayers(layers) {
  return layers.map((layer, index, array) => (
    validateLayer(layer, index, array)
  ));
}

function validateLayer(layer, index, layers) {
  let validations = {};

  switch (layer.type) {
    case COPPER:
      validations = validateCopper(layer, index, layers)
      break;
    case PREPREG:
      // validations = validatePrepreg(layer);
      break;
    case CORE:
      // validations = validateCore(layer);
      break;
    default:
      break;
  }

  return validations;
}



// WEBPACK FOOTER //
// ./src/lib/stackup.js